import {useCallback} from 'react';
import {useStoreApi} from 'reactflow';

import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

export interface NodeDimensions {
  width: number;
  height: number;
}

function getDefaultNodeDimensions(): NodeDimensions {
  const fallbackNodeHeight = COMMON_NODE_WIDTH * 2;
  return {width: COMMON_NODE_WIDTH, height: fallbackNodeHeight};
}

/**
 * Reads measured node dimensions from the ReactFlow store, falling back to default
 * dimensions when a node is missing from the store or its measurements are zero or
 * undefined. Useful for callers that need a node's rendered size without subscribing
 * to ReactFlow's reactive store updates.
 */
export function useNodeDimensions(): {
  getMeasuredDimensionsFromStore: (nodeId: string) => NodeDimensions;
} {
  const reactFlowStore = useStoreApi();

  const getMeasuredDimensionsFromStore = useCallback(
    (nodeId: string): NodeDimensions => {
      const {nodeInternals} = reactFlowStore.getState();
      const nodeInternal = nodeInternals.get(nodeId);

      const width = nodeInternal?.width;
      const height = nodeInternal?.height;
      if (width != null && height != null && width > 0 && height > 0) {
        return {width, height};
      }

      return getDefaultNodeDimensions();
    },
    [reactFlowStore]
  );

  return {getMeasuredDimensionsFromStore};
}
